import React from 'react';
import { Link } from 'react-router-dom';
import { useTenant } from '../../../context/TenantContext';
import { parseEm } from '../inlineEm';
import { ShotFrame } from './Shot';
import { BOARD_CHOICE, HERO } from './content';

/**
 * The claim's emphasis is the brand pink.
 *
 * It used to rotate through the three lab accents — run N took lab N's colour
 * — which was right for a claim that named all three products in order. The
 * claim it actually carries names the PRODUCT ("We think *Kula Revise* is the
 * best way…"), one run, so the rotation only ever reached its first entry and
 * painted the brand name gold. On this page gold means "this one is free" and
 * nothing else; the brand is pink. Bring the rotation back with the copy that
 * needed it, not before.
 */
const CLAIM_ACCENT = 'rv-c-brand';

/**
 * `text-wrap: balance` (app.css) decides most headline wraps on its own, but
 * the pun headline needs its break to land on the sentence boundary, not
 * wherever balance puts it — "So" stranded on the first line reads as the
 * setup and payoff running together. `\n` in the copy is a hard break: split
 * on it and parseEm each line separately, so an `*emphasis*` clause can still
 * start clean on its own line rather than being split mid-run.
 */
const renderHeadline = (headline: string) => {
  const lines = headline.split('\n');
  return lines.map((line, i) => (
    <React.Fragment key={i}>
      {i > 0 && <br />}
      {parseEm(line)}
    </React.Fragment>
  ));
};

export const ReviseHero: React.FC = () => {
  const { tenant } = useTenant();
  const hero = tenant?.branding.marketing?.hero ?? {};

  const claim = hero.claim || HERO.claim;
  const headline = hero.headline || HERO.headline || '';
  const shots = hero.shots?.length ? hero.shots : (HERO.shots ?? []);

  return (
    <header className="rv-hero rv-band-centre" id="top">
      <div className="rv-wrap">
        <div className="rv-hero-copy">
          {claim && (
            <p className="rv-claim">
              {parseEm(claim, (children, key) => (
                <em key={key} className={CLAIM_ACCENT}>
                  {children}
                </em>
              ))}
            </p>
          )}

          <h1>{renderHeadline(headline)}</h1>

          {/* The board choice — see BOARD_CHOICE in content.ts. The first
              decision the page asks for, at equal weight: two cards, same
              size, same shape, and each carrying its OWN action. The NSC
              card IS the try-a-lesson CTA (straight to the demo at `#try`);
              the IEB card leaves for /ieb. The hero ends here — a shared
              button under both cards was a mis-click waiting for whichever
              board it didn't belong to. */}
          <div className="rv-choice" role="group" aria-label={BOARD_CHOICE.kicker}>
            <p className="rv-choice-kicker">{BOARD_CHOICE.kicker}</p>
            <div className="rv-choice-cards">
              <a className="rv-choice-card" href={BOARD_CHOICE.nsc.href}>
                <span className="rv-choice-word">{BOARD_CHOICE.nsc.word}</span>
                <span className="rv-choice-line">{BOARD_CHOICE.nsc.line}</span>
                <span className="rv-choice-go">
                  {BOARD_CHOICE.nsc.go} <span aria-hidden="true">&darr;</span>
                </span>
              </a>
              <Link className="rv-choice-card" to={BOARD_CHOICE.ieb.href}>
                <span className="rv-choice-word">{BOARD_CHOICE.ieb.word}</span>
                <span className="rv-choice-line">{BOARD_CHOICE.ieb.line}</span>
                <span className="rv-choice-go">
                  {BOARD_CHOICE.ieb.go} <span aria-hidden="true">&rarr;</span>
                </span>
              </Link>
            </div>
          </div>
        </div>

        {shots.length > 0 && (
          <div className="rv-hero-shots">
            {shots.map((shot, i) => (
              <div
                key={shot.src}
                className="rv-figure"
                data-accent={(['study', 'essay', 'practice'] as const)[i % 3]}
              >
                <ShotFrame shot={shot} eager={i === 0} />
              </div>
            ))}
          </div>
        )}
      </div>
    </header>
  );
};
